import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import axios from "axios";

const API_URL = "/api";
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const savedUser = (localStorage.getItem("erp_user") || localStorage.getItem("user"));
      return savedUser ? JSON.parse(savedUser) : null;
    } catch {
      localStorage.removeItem("erp_user");
    localStorage.removeItem("user");
      return null;
    }
  });

  const [token, setToken] = useState(
    () => (localStorage.getItem("erp_token") || localStorage.getItem("token") || null)
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const interceptorId = axios.interceptors.request.use(
      (config) => {
        const storedToken = (localStorage.getItem("erp_token") || localStorage.getItem("token"));
        if (storedToken) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${storedToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    return () => axios.interceptors.request.eject(interceptorId);
  }, []);

  const saveAuthenticatedSession = useCallback((data) => {
    const receivedToken =
      data?.token || data?.accessToken ||
      data?.data?.token || data?.data?.accessToken;
    const receivedUser = data?.user || data?.data?.user || null;

    if (!receivedToken) {
      throw new Error("The server did not return an authentication token.");
    }

    localStorage.setItem("erp_token", receivedToken);
    localStorage.setItem("token", receivedToken);
    setToken(receivedToken);

    if (receivedUser) {
      localStorage.setItem("erp_user", JSON.stringify(receivedUser));
      localStorage.setItem("user", JSON.stringify(receivedUser));
      setUser(receivedUser);
    }

    return { success: true, user: receivedUser, token: receivedToken,
      message: data?.message || "Login successful." };
  }, []);

  const getErrorMessage = (error, fallback) =>
    error.response?.data?.message ||
    error.response?.data?.error ||
    (error.code === "ERR_NETWORK"
      ? "Cannot reach the ERP API. Check the connection and try again."
      : error.code === "ECONNABORTED"
      ? "The request timed out. Please try again."
      : error.message || fallback);

  const login = useCallback(async (username, password) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/auth/login`,
        { username: String(username).trim(), password },
        { headers: { "Content-Type": "application/json" }, timeout: 20000 }
      );

      const data = response.data;
      if (data?.success !== true) {
        throw new Error(data?.message || "Login failed.");
      }

      // OTP challenge: do not store a token or mark the user authenticated.
      if (data.requiresOtp === true) {
        return {
          success: true,
          requiresOtp: true,
          challengeId: data.challengeId,
          message: data.message || "Enter the OTP sent to your email.",
        };
      }

      // Never create an authenticated session from password-only login.
      return {
        success: false,
        message: "OTP verification is required to complete login."
      };
    } catch (error) {
      return { success: false, message: getErrorMessage(error, "Unable to log in.") };
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyOtp = useCallback(async (challengeId, otp) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/auth/verify-otp`,
        { challengeId, otp: String(otp).trim() },
        { headers: { "Content-Type": "application/json" }, timeout: 20000 }
      );

      const data = response.data;
      if (data?.success !== true) {
        throw new Error(data?.message || "OTP verification failed.");
      }

      return saveAuthenticatedSession(data);
    } catch (error) {
      return {
        success: false,
        message: getErrorMessage(error, "Unable to verify OTP."),
        pendingApproval: error.response?.data?.pendingApproval === true
      };
    } finally {
      setLoading(false);
    }
  }, [saveAuthenticatedSession]);

  const resendOtp = useCallback(async (challengeId) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/auth/resend-otp`,
        { challengeId },
        { headers: { "Content-Type": "application/json" }, timeout: 20000 }
      );

      const data = response.data;
      if (data?.success !== true) {
        throw new Error(data?.message || "Unable to resend OTP.");
      }

      return { success: true, message: data.message || "A new OTP was sent." };
    } catch (error) {
      return { success: false, message: getErrorMessage(error, "Unable to resend OTP.") };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("erp_token");
    localStorage.removeItem("token");
    localStorage.removeItem("erp_user");
    localStorage.removeItem("user");
    setToken(null);
    setUser(null);
  }, []);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: Boolean(token),
    login,
    verifyOtp,
    resendOtp,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside an AuthProvider.");
  return context;
};

export default AuthContext;
